'use client';

import type { ReactNode } from 'react';
import { RadioIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { MetaDefault, MonoSmall } from '../../../../../components/typography';
import type { CardRenderContext } from '../../tool-config-types';
import { FlatToolCard } from '../shared/flat-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import {
  readStringValue,
  toPrettyText,
  toRecord,
} from '../shared/network-tool-card-utils';
import { renderInteractshResult } from './interactsh-result-renderer';
import { IconButton } from '../../../../../components/icon-button';
import { Tooltip } from '../../../../../components/tooltip';

function readInteractionSelectionId(
  output: Record<string, unknown> | undefined,
): string | undefined {
  const interactions = output?.interactions;

  if (!Array.isArray(interactions) || interactions.length === 0) {
    return undefined;
  }

  const firstInteraction = interactions[0];

  if (!firstInteraction || typeof firstInteraction !== 'object') {
    return undefined;
  }

  const interactionRecord = firstInteraction as Record<string, unknown>;
  const id = interactionRecord.id;

  if (typeof id === 'string' && id.trim().length > 0) {
    return id;
  }

  const uniqueId = interactionRecord.uniqueId;

  if (typeof uniqueId === 'string' && uniqueId.trim().length > 0) {
    return uniqueId;
  }

  return undefined;
}

function InteractshCard({ ctx }: { ctx: CardRenderContext }): ReactNode {
  const t = useTranslations('designSystem.chat.interactsh');
  const input = toRecord(ctx.toolCallData.input);
  const output = toRecord(ctx.toolCallData.output);
  const domain =
    readStringValue(output, ['url', 'domain', 'dynamicUrl']) ??
    readStringValue(input, ['url', 'domain', 'dynamicUrl']);
  const sessionId =
    readStringValue(input, ['sessionId', 'session_id']) ??
    readStringValue(output, ['sessionId', 'session_id']);
  const interactionId = readInteractionSelectionId(output);
  const isCompleted = ctx.toolCallData.status === 'completed';
  const isRunning =
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';
  const error = ctx.toolCallData.error;
  const actionContent =
    domain || sessionId
      ? [
          domain ? `url: ${domain}` : undefined,
          sessionId ? `session: ${sessionId}` : undefined,
        ]
          .filter(Boolean)
          .join('\n')
      : toPrettyText(ctx.toolCallData.input);
  const result = ctx.toolCallData.output
    ? renderInteractshResult({
        content: ctx.toolCallData.output,
      })
    : undefined;
  const openInInteractButton =
    isCompleted && domain ? (
      <Tooltip title={t('openInInteract')} openDelay={500} closeDelay={0}>
        <IconButton
          size="xs"
          variant="ghost"
          aria-label={t('openInInteract')}
          onClick={(event) => {
            event.stopPropagation();
            window.dispatchEvent(
              new CustomEvent('terra-open-interact-session', {
                detail: {
                  domain,
                  interactionId,
                },
                bubbles: true,
              }),
            );
          }}
        >
          <RadioIcon size={11} />
        </IconButton>
      </Tooltip>
    ) : undefined;

  return (
    <FlatToolCard
      title={t('interactionsCheck')}
      isRunning={isRunning}
      durationMs={ctx.toolCallData.timing?.durationMs}
      headerActions={
        <>
          {getHITLArtifactHeaderActions(ctx)}
          {openInInteractButton}
        </>
      }
    >
      {actionContent ? (
        <MonoSmall color="fg.muted" whiteSpace="pre-wrap" wordBreak="break-all">
          {actionContent}
        </MonoSmall>
      ) : null}
      {error ? (
        <MetaDefault color="fg.error" whiteSpace="pre-wrap">
          {error}
        </MetaDefault>
      ) : (
        result
      )}
    </FlatToolCard>
  );
}

export function renderInteractshCard(ctx: CardRenderContext): ReactNode {
  return <InteractshCard ctx={ctx} />;
}
